import { createPortal } from 'react-dom';
import { useLayoutEffect, useState, type ReactNode, type RefObject } from 'react';

type Props = {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  popoverRef: RefObject<HTMLDivElement | null>;
  id?: string;
  className: string;
  ariaLabel: string;
  estimatedHeight?: number;
  children: ReactNode;
};

export default function CalendarPopoverPortal({
  open,
  anchorRef,
  popoverRef,
  id,
  className,
  ariaLabel,
  estimatedHeight = 300,
  children,
}: Props) {
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;

    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const popoverEl = popoverRef.current;
      const width = popoverEl?.offsetWidth ?? (className.includes('dual') ? 480 : 252);
      const height = popoverEl?.offsetHeight ?? estimatedHeight;

      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const placeAbove = spaceBelow < height && rect.top > height + 12;
      const top = placeAbove ? rect.top - height - 6 : rect.bottom + 6;

      let left = rect.left;
      if (left + width > window.innerWidth - 12) {
        left = window.innerWidth - width - 12;
      }
      left = Math.max(12, left);

      const isDual = className.includes('dual');

      setStyle({
        position: 'fixed',
        top: Math.max(12, top),
        left,
        zIndex: 10000,
        visibility: 'visible',
        ...(isDual ? { minWidth: rect.width } : {}),
      });
    };

    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, popoverRef, estimatedHeight, className]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popoverRef}
      id={id}
      className={className}
      role="dialog"
      aria-label={ariaLabel}
      style={style}
    >
      {children}
    </div>,
    document.body,
  );
}
